import React, { useState, useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import Menu from '@material-ui/core/Menu';

import { GUIDE_TYPE } from 'yeself-common/ModelConstant';
import { LayerId } from 'yeself-common/typings/guide/LayerCommon';

import { makeSelectLayerType, makeSelectIsLayerWithRelatedRule } from 'Console/layers/LayersSelector';

import GuideListItemGeneralMenuItems from 'Console/components/applications/detail/guides/GuideListItemGeneralMenuItems';
import GuideListItemRecordingMenuItems from 'Console/components/applications/detail/guides/GuideListItemRecordingMenuItems';
import GuideListItemAugmentationMenuItems from 'Console/components/applications/detail/guides/GuideListItemAugmenationMenuItems';
import ConfirmDialog from 'Console/common/components/ConfirmDialog';

const ITEM_HEIGHT = 48;

type GuideListItemMenuProps = {
    isOpen: boolean;
    anchorEl: React.ComponentProps<typeof Menu>['anchorEl'];
    onClose: React.ComponentProps<typeof Menu>['onClose'];
    onDuplicateGuide: () => void;
    handleUpdateState: (
        arg: unknown
    ) => React.ComponentProps<typeof GuideListItemGeneralMenuItems>['onDuplicateGuide'];
    guide: unknown;
    layerId: LayerId;
};

const RenderSwitchGuideMenuList = ({
    guideType,
    handleUpdateState,
    guide,
    onClose,
    isLayerWithRelatedRule,
    handleDuplicateConfirmDialogOpen,
    handleDuplicateGuide,
}) => {
    switch (guideType) {
        case GUIDE_TYPE.APPLICATION_AUGMENTATION:
            return (
                <GuideListItemAugmentationMenuItems
                    handleUpdateState={handleUpdateState}
                    onDuplicateGuide={
                        isLayerWithRelatedRule ? handleDuplicateConfirmDialogOpen : handleDuplicateGuide
                    }
                />
            );
        case GUIDE_TYPE.RECORDING:
            return <GuideListItemRecordingMenuItems guide={guide} onClose={onClose} />;

        default:
            return (
                <GuideListItemGeneralMenuItems
                    handleUpdateState={handleUpdateState}
                    onDuplicateGuide={
                        isLayerWithRelatedRule ? handleDuplicateConfirmDialogOpen : handleDuplicateGuide
                    }
                />
            );
    }
};

const GuideListItemMenu = ({
    isOpen,
    handleUpdateState,
    anchorEl,
    onClose,
    onDuplicateGuide,
    guide,
    layerId,
}: GuideListItemMenuProps): React.ReactElement => {
    const { formatMessage } = useIntl();

    const [isDuplicateConfirmDialogOpen, setDuplicateConfirmDialogOpen] = useState(false);

    const selectLayerType = useMemo(makeSelectLayerType, []);
    const selectIsLayerWithRelatedRule = useMemo(makeSelectIsLayerWithRelatedRule, []);

    const layerType = useSelector((state) => selectLayerType(state, layerId));
    const isLayerWithRelatedRule = useSelector((state) => selectIsLayerWithRelatedRule(state, layerId));

    const handleDuplicateConfirmDialogClose = useCallback(() => {
        setDuplicateConfirmDialogOpen(false);
    }, []);

    const handleDuplicateConfirmDialogOpen = useCallback(() => {
        setDuplicateConfirmDialogOpen(true);
    }, []);

    const handleDuplicateConfirmDialogConfirm = useCallback(() => {
        onDuplicateGuide();
        setDuplicateConfirmDialogOpen(false);
    }, [onDuplicateGuide]);

    const handleDuplicateGuide = useCallback(onDuplicateGuide, [onDuplicateGuide]);

    return (
        <>
            <Menu
                id="other-actions-menu"
                open={isOpen}
                anchorEl={anchorEl}
                onClose={onClose}
                PaperProps={{
                    style: {
                        maxHeight: ITEM_HEIGHT * 4.5,
                    },
                }}
            >
                <RenderSwitchGuideMenuList
                    guideType={layerType}
                    handleUpdateState={handleUpdateState}
                    guide={guide}
                    onClose={onClose}
                    isLayerWithRelatedRule={isLayerWithRelatedRule}
                    handleDuplicateConfirmDialogOpen={handleDuplicateConfirmDialogOpen}
                    handleDuplicateGuide={handleDuplicateGuide}
                />
            </Menu>
            <ConfirmDialog
                open={isDuplicateConfirmDialogOpen}
                title={
                    <FormattedMessage
                        id="Guides.duplicateGuideWarningTitle"
                        defaultMessage="Guide contains rules"
                    />
                }
                content={formatMessage({
                    id: 'Guides.duplicateGuideWarningContent',
                    defaultMessage:
                        'Guide you are trying to duplicate contains rules with relation to other guides.',
                })}
                confirmLabel={
                    <FormattedMessage
                        id="Guides.menuItem.duplicateGuide"
                        defaultMessage="Duplicate guide"
                    />
                }
                cancelLabel={<FormattedMessage id="Buttons.cancel" />}
                disableBackdropClick={false}
                onConfirm={handleDuplicateConfirmDialogConfirm}
                onCancel={handleDuplicateConfirmDialogClose}
                onClose={handleDuplicateConfirmDialogClose}
            />
        </>
    );
};

export default GuideListItemMenu;
